/**
 * Feasibility + cost-delta evaluator for post-solve route editing.
 *
 * Mirrors the backend solver's semantics (route-optimizer-back-end
 * app/pickup_delivery_model.py + app/vrp_data.py) — every rule here cites the
 * behaviour it reproduces. Two parallel ledgers run through the forward pass:
 *
 *  - REPORTED ledger (floats): un-ceiled duration/cost/distance exactly as the
 *    backend reports them in route_stops rows and the Results Summary. All
 *    user-facing deltas and regenerated row values come from this ledger.
 *  - FEASIBILITY ledger (ints): the solver's own arithmetic —
 *    transit(i→j) = max(1, ceil(travel) + serviceDuration(i)), arrival checked
 *    against windows BEFORE service, unlimited waiting. All violation verdicts
 *    come from this ledger, so an edit the UI calls clean is one the solver's
 *    integer model would also accept.
 *
 * Deliberately mirrored quirks (do not "fix" without changing the solver):
 *  - exact* time windows are IGNORED (the solver never reads them).
 *  - travelDurationFromPreviousStop / travelDistanceFromPreviousStop use the
 *    type-AGNOSTIC first-row lookup; cost uses the type-AWARE lookup.
 *  - The vehicle shift window bounds only the route END, and only when the
 *    vehicle's minStartTime is set.
 *  - The excess-travel cap compares delivery arrival minus pickup arrival
 *    (which includes intermediate service and waiting) against
 *    cap + direct time from the type-agnostic matrix.
 *  - The capacity dimension is skipped entirely when there are no demands, no
 *    finite capacities, or every capacity covers the total demand.
 */

import { generateUUID } from 'store/functions/geoJsonFunctions.js';
import { windowBoundToMinutes, minutesToClockStr } from './timeConversion';
import { getCostIndex } from './costIndex';

export const VIOLATIONS = {
  TIME_WINDOW: 'TIME_WINDOW',
  CAPACITY: 'CAPACITY',
  VEHICLE_TYPE: 'VEHICLE_TYPE',
  EXCESS_TRAVEL: 'EXCESS_TRAVEL',
  SHIFT_END: 'SHIFT_END',
  UNREACHABLE_LEG: 'UNREACHABLE_LEG',
  PAIRING_ORDER: 'PAIRING_ORDER',
};

const DEPOT_TYPES = new Set(['route_start', 'route_end']);
export const IMMOVABLE_STOP_TYPES = new Set(['route_start', 'route_end', 'restock']);

/*
Stop types the solver models as a POSITIVE (loading) node in vrp_data.

Both values mean the same node. Vehicle-routing solves used to report their
single-node visits as `pickup`; newer ones report `location_visit`. Stored
results and saved scenarios keep whichever value they were solved with, so
both are valid indefinitely and neither is a legacy alias to be migrated.

Reading this as a set rather than `=== 'pickup'` is what keeps an unrecognised
type from silently weighing nothing: a vehicle-routing stop whose demand
evaluates to 0 removes the load from the capacity ledger, so an over-capacity
edit stops being flagged and the route cards report a comfortable vehicle.
Nothing errors — the number is just wrong.
*/
const PICKUP_STOP_TYPES = new Set(['pickup', 'location_visit']);

const TRIP_COLS = {
  pickup: {
    min: { int: 'minPickupTimeInt', str: 'minPickupTimeStr', day: 'minPickupDayInt' },
    max: { int: 'maxPickupTimeInt', str: 'maxPickupTimeStr', day: 'maxPickupDayInt' },
  },
  delivery: {
    min: { int: 'minDeliveryTimeInt', str: 'minDeliveryTimeStr', day: 'minDeliveryDayInt' },
    max: { int: 'maxDeliveryTimeInt', str: 'maxDeliveryTimeStr', day: 'maxDeliveryDayInt' },
  },
};
const LOCATION_COLS = {
  min: { int: 'minArrivalTimeInt', str: 'minArrivalTimeStr', day: 'minArrivalDayInt' },
  max: { int: 'maxArrivalTimeInt', str: 'maxArrivalTimeStr', day: 'maxArrivalDayInt' },
};
const VEHICLE_COLS = {
  minStart: { int: 'minStartTimeInt', str: 'minStartTimeStr', day: 'minStartDayInt' },
  maxEnd: { int: 'maxEndTimeInt', str: 'maxEndTimeStr', day: 'maxEndDayInt' },
};

const num = (v) => {
  const n = Number(v);
  return v == null || v === '' || !Number.isFinite(n) ? null : n;
};

/** Build the lookup context the evaluator needs from store data. */
export function buildEvalContext(data, timeUnit) {
  const tripsById = new Map((data.trips || []).map((t) => [String(t.UUID), t]));
  const locationsById = new Map((data.locations || []).map((l) => [String(l.UUID), l]));
  const vehiclesById = new Map((data.vehicles || []).map((v) => [String(v.vehicleId), v]));
  const vehicleTypesById = new Map((data.vehicle_types || []).map((vt) => [String(vt.UUID), vt]));
  const cost = getCostIndex(data.travel_costs || []);

  // Mirror add_capacity_constraint's skip conditions (pickup_delivery_model.py:83-100):
  // no positive demands, no finite capacities, or every capacity >= total demand
  // means the solver never adds the dimension — so we must not flag capacity either.
  const demandSource = (data.trips || []).length > 0 ? data.trips : data.locations || [];
  const totalDemand = demandSource.reduce((acc, r) => acc + Math.max(0, num(r.demand) ?? 0), 0);
  const capacities = (data.vehicle_types || []).map((vt) => num(vt.capacity));
  const finiteCaps = capacities.filter((c) => c != null);
  const capacityActive =
    totalDemand > 0 && finiteCaps.length > 0 && !finiteCaps.every((c) => c >= totalDemand);

  const vehicleInfo = (vehicleId) => {
    const v = vehiclesById.get(String(vehicleId));
    if (!v) return null;
    const vt = v.vehicleTypeId != null ? vehicleTypesById.get(String(v.vehicleTypeId)) : null;
    return {
      row: v,
      vehicleTypeId: v.vehicleTypeId != null ? String(v.vehicleTypeId) : null,
      capacity: vt ? num(vt.capacity) : null,
      fixedCost: num(v.fixedCost) ?? 0,
      minStart: windowBoundToMinutes(v, VEHICLE_COLS.minStart, timeUnit),
      maxEnd: windowBoundToMinutes(v, VEHICLE_COLS.maxEnd, timeUnit),
      startLocationId: v.startLocationId != null ? String(v.startLocationId) : null,
      color: v.color || null,
    };
  };

  return { tripsById, locationsById, vehiclesById, vehicleTypesById, cost, capacityActive, vehicleInfo, timeUnit };
}

/**
 * Time window (minutes) for a stop row; [null, null] = unconstrained.
 *
 * Exported so the results UI can show the same window the feasibility check
 * enforces — including the deliberate omission of the exact* columns, which
 * the solver never reads. Drawing those would promise a constraint that
 * nothing enforces.
 */
export function windowFor(row, ctx) {
  const trip = row.tripId != null ? ctx.tripsById.get(String(row.tripId)) : null;
  if (trip) {
    const cols = row.stopType === 'delivery' ? TRIP_COLS.delivery : TRIP_COLS.pickup;
    return [
      windowBoundToMinutes(trip, cols.min, ctx.timeUnit),
      windowBoundToMinutes(trip, cols.max, ctx.timeUnit),
    ];
  }
  // Vehicle-routing rows: trips are backend-synthesized (integer tripIds not in
  // the trips table); windows live on the location (vrp_data _set_trips_from_locations).
  const loc = row.locationId != null ? ctx.locationsById.get(String(row.locationId)) : null;
  if (loc) {
    return [
      windowBoundToMinutes(loc, LOCATION_COLS.min, ctx.timeUnit),
      windowBoundToMinutes(loc, LOCATION_COLS.max, ctx.timeUnit),
    ];
  }
  return [null, null];
}

/** Signed demand for the capacity ledger: +pickup or location_visit / −delivery (vrp_data signed nodes). */
function signedDemand(row, ctx) {
  const trip = row.tripId != null ? ctx.tripsById.get(String(row.tripId)) : null;
  const loc = row.locationId != null ? ctx.locationsById.get(String(row.locationId)) : null;
  const magnitude =
    (trip && num(trip.demand)) ?? (loc && num(loc.demand)) ?? Math.abs(num(row.capacityServed) ?? 0);
  if (row.stopType === 'delivery') return -magnitude;
  if (PICKUP_STOP_TYPES.has(row.stopType)) return magnitude;
  return 0;
}

/**
 * Capacity ledger for one vehicle's ordered stops, without the timing
 * simulation evaluateRoute runs. Same rules — signed demand, restock resets —
 * so a load shown on a route card can never disagree with the CAPACITY
 * violation verdict computed from the same rows.
 *
 * `peak` is the most carried at once, which is what capacity actually limits.
 * With resupply the route `total` can far exceed the vehicle's capacity while
 * every individual leg fits, so the two are reported separately.
 */
export function computeRouteLoad(orderedStops, ctx) {
  let load = 0;
  let peak = 0;
  let total = 0;
  let restockCount = 0;
  let hasDemand = false;

  for (const row of orderedStops || []) {
    if (row == null) continue;
    if (row.stopType === 'restock') {
      load = 0;
      restockCount += 1;
      continue;
    }
    const d = signedDemand(row, ctx);
    if (d !== 0) hasDemand = true;
    if (d > 0) total += d;
    load += d;
    if (load > peak) peak = load;
  }

  return { peak, total, restockCount, hasDemand };
}

/** Allowed vehicle-type ids for a stop (empty array = unrestricted). */
function allowedTypes(row, ctx) {
  const trip = row.tripId != null ? ctx.tripsById.get(String(row.tripId)) : null;
  const src = trip || (row.locationId != null ? ctx.locationsById.get(String(row.locationId)) : null);
  const ids = src && Array.isArray(src.vehicleTypeIds) ? src.vehicleTypeIds.filter((x) => x != null) : [];
  return ids.map(String);
}

/**
 * Forward pass over one vehicle's FULL ordered stop sequence (including
 * route_start / route_end rows).
 *
 * anchorMinutes: time at the first row (its departure for a route_start row).
 *  - Golden check passes the backend-reported value to validate propagation.
 *  - Edits pass the earliest feasible start (vehicle minStart ?? 0), giving an
 *    earliest-arrival schedule — the standard correct feasibility test when
 *    waiting is free, and a deterministic recomputed timetable.
 */
export function evaluateRoute(vehicleId, orderedStops, ctx, { anchorMinutes = null } = {}) {
  const vehicle = ctx.vehicleInfo(vehicleId);
  const vehicleTypeId = vehicle ? vehicle.vehicleTypeId : null;
  const violations = [];
  const annotated = [];
  const pickupArrivalInt = new Map();
  const deliveredTrips = new Set();

  let tInt = anchorMinutes ?? (vehicle && vehicle.minStart != null ? vehicle.minStart : 0);
  const startMinutes = tInt;
  let tRep = tInt;
  let load = 0;
  const totals = { cost: 0, duration: 0, distance: 0 };

  for (let i = 0; i < orderedStops.length; i++) {
    const row = orderedStops[i];
    const prev = i > 0 ? orderedStops[i - 1] : null;
    let waitInt = 0;

    if (prev) {
      const sameLocation = String(prev.locationId) === String(row.locationId);
      const aware = ctx.cost.typeAware(prev.locationId, row.locationId, vehicleTypeId);
      const agnostic = ctx.cost.typeAgnostic(prev.locationId, row.locationId);
      if (!aware && !sameLocation) {
        violations.push({
          type: VIOLATIONS.UNREACHABLE_LEG,
          message: `No travel cost from ${prev.locationName || prev.locationId} to ${row.locationName || row.locationId}`,
          entities: { fromLocationId: prev.locationId, toLocationId: row.locationId, vehicleId },
        });
      }
      const travelInt = sameLocation ? 0 : Math.ceil(num(aware && aware.duration) ?? 0);
      const travelRep = sameLocation ? 0 : num(agnostic && agnostic.duration) ?? 0;
      const service = num(prev.serviceDuration) ?? 0;

      // Solver: transit = max(1, ceil(travel) + service) — pickup_delivery_model.py:423-429
      const earliestInt = tInt + Math.max(1, travelInt + service);
      const earliestRep = tRep + travelRep + service;
      const [ew, lw] = windowFor(row, ctx);
      const arrivalInt = ew != null ? Math.max(earliestInt, ew) : earliestInt;
      waitInt = arrivalInt - earliestInt;
      // Wait delays the reported clock identically (backend departure math).
      const arrivalRep = ew != null ? Math.max(earliestRep, ew) : earliestRep;

      if (lw != null && arrivalInt > lw && row.stopType !== 'route_end') {
        violations.push({
          type: VIOLATIONS.TIME_WINDOW,
          message: `${row.locationName || row.locationId}: would arrive at ${minutesToClockStr(arrivalInt)} — after the window closes (${minutesToClockStr(lw)})`,
          entities: { stopKey: stopKeyOf(row), tripId: row.tripId, locationId: row.locationId, vehicleId, arrivalInt, windowClose: lw },
        });
      }

      totals.cost += sameLocation ? 0 : num(aware && aware.cost) ?? 0;
      totals.duration += travelRep;
      totals.distance += sameLocation ? 0 : num(agnostic && agnostic.distance) ?? 0;
      tInt = arrivalInt;
      tRep = arrivalRep;
    }

    // Capacity ledger — restock resets it (resupply anchor; immovable in v1).
    if (row.stopType === 'restock') load = 0;
    else load += signedDemand(row, ctx);
    if (ctx.capacityActive && vehicle && vehicle.capacity != null && load > vehicle.capacity) {
      violations.push({
        type: VIOLATIONS.CAPACITY,
        message: `${row.locationName || row.locationId}: load ${load} exceeds capacity ${vehicle.capacity}`,
        entities: { stopKey: stopKeyOf(row), tripId: row.tripId, vehicleId, load, capacity: vehicle.capacity },
      });
    }

    // Vehicle-type restriction (SetAllowedVehiclesForIndex).
    if (!DEPOT_TYPES.has(row.stopType)) {
      const allowed = allowedTypes(row, ctx);
      if (allowed.length > 0 && vehicleTypeId != null && !allowed.includes(vehicleTypeId)) {
        violations.push({
          type: VIOLATIONS.VEHICLE_TYPE,
          message: `${row.locationName || row.locationId}: this vehicle's type is not allowed for this ${row.tripId != null ? 'trip' : 'location'}`,
          entities: { stopKey: stopKeyOf(row), tripId: row.tripId, locationId: row.locationId, vehicleId, allowedTypeIds: allowed },
        });
      }
    }

    // Pickup/delivery pairing + excess-travel cap.
    if (row.stopType === 'pickup' && row.tripId != null) pickupArrivalInt.set(String(row.tripId), tInt);
    if (row.stopType === 'delivery' && row.tripId != null) {
      const key = String(row.tripId);
      deliveredTrips.add(key);
      if (!pickupArrivalInt.has(key)) {
        violations.push({
          type: VIOLATIONS.PAIRING_ORDER,
          message: `Delivery for trip would occur before (or without) its pickup on this route`,
          entities: { stopKey: stopKeyOf(row), tripId: row.tripId, vehicleId },
        });
      } else {
        const trip = ctx.tripsById.get(key);
        const cap = trip ? num(trip.excessTravelTime) : null;
        if (cap != null) {
          const pickRow = orderedStops.find((s) => String(s.tripId) === key && s.stopType === 'pickup');
          const direct = pickRow ? ctx.cost.typeAgnostic(pickRow.locationId, row.locationId) : null;
          const directInt = Math.ceil(num(direct && direct.duration) ?? 0);
          const ride = tInt - pickupArrivalInt.get(key);
          if (ride > cap + directInt) {
            violations.push({
              type: VIOLATIONS.EXCESS_TRAVEL,
              message: `Trip ride time ${ride}m exceeds its cap (${cap}m over the direct ${directInt}m)`,
              entities: { stopKey: stopKeyOf(row), tripId: row.tripId, vehicleId, rideMinutes: ride, capMinutes: cap, directMinutes: directInt },
            });
          }
        }
      }
    }

    annotated.push({ row, arrivalInt: prev ? tInt : null, arrivalRep: prev ? tRep : null, waitInt: 0, load, index: i });
    // Wait is attributed to the PREVIOUS stop (backend: departure−arrival−service).
    if (prev && waitInt > 0) annotated[i - 1].waitInt += waitInt;
  }

  // Vehicle shift window: solver bounds only the route END cumul, and only
  // when minStartTime is non-null (add_vehicle_time_constraints:159-188).
  if (vehicle && vehicle.minStart != null && vehicle.maxEnd != null && tInt > vehicle.maxEnd) {
    violations.push({
      type: VIOLATIONS.SHIFT_END,
      message: `Route would end at ${minutesToClockStr(tInt)} — after the vehicle's shift end (${minutesToClockStr(vehicle.maxEnd)})`,
      entities: { vehicleId, endInt: tInt, maxEnd: vehicle.maxEnd },
    });
  }

  return { vehicleId, stops: annotated, violations, totals, endInt: tInt, startMinutes };
}

/** Stable identity for a stop row across reloads (row ids are regenerated). */
export function stopKeyOf(row) {
  return `${row.tripId ?? ''}|${row.locationId ?? ''}|${row.stopType}`;
}

/** Group route_stops rows by vehicle, ordered by stopNumber. */
export function groupRouteStops(routeStops) {
  const byVehicle = new Map();
  for (const row of routeStops || []) {
    if (row == null || row.vehicleId == null) continue;
    const key = String(row.vehicleId);
    if (!byVehicle.has(key)) byVehicle.set(key, []);
    byVehicle.get(key).push(row);
  }
  for (const rows of byVehicle.values()) {
    rows.sort((a, b) => (num(a.stopNumber) ?? 0) - (num(b.stopNumber) ?? 0));
  }
  return byVehicle;
}

/**
 * Regenerate backend-shaped route_stops rows from an evaluated route.
 * Preserves the backend's conventions: 0-based stopNumber, travelCostTo =
 * type-aware cost to the NEXT stop (last = 0), type-agnostic
 * duration/distance FROM the previous stop, arrival null at route_start and
 * departure null at route_end, "%H:%M:%S" strings in clock mode.
 */
export function regenerateRouteRows(evaluated, ctx) {
  const vehicle = ctx.vehicleInfo(evaluated.vehicleId);
  const vehicleTypeId = vehicle ? vehicle.vehicleTypeId : null;
  const clock = ctx.timeUnit !== 'integer';
  const n = evaluated.stops.length;

  return evaluated.stops.map(({ row, arrivalInt, waitInt }, i) => {
    const next = i + 1 < n ? evaluated.stops[i + 1].row : null;
    const prev = i > 0 ? evaluated.stops[i - 1].row : null;
    const sameAsNext = next && String(next.locationId) === String(row.locationId);
    const sameAsPrev = prev && String(prev.locationId) === String(row.locationId);
    const aware = next && !sameAsNext ? ctx.cost.typeAware(row.locationId, next.locationId, vehicleTypeId) : null;
    const agnostic = prev && !sameAsPrev ? ctx.cost.typeAgnostic(prev.locationId, row.locationId) : null;
    const service = num(row.serviceDuration) ?? 0;
    const isStart = row.stopType === 'route_start';
    const isEnd = row.stopType === 'route_end';
    // Backend conventions: route_start has no arrival and departs at the route's
    // anchor (+ any wait attributed to it); route_end has no departure; work
    // stops depart after service + wait.
    let depInt = null;
    if (isStart) depInt = evaluated.startMinutes + waitInt;
    else if (!isEnd && arrivalInt != null) depInt = arrivalInt + service + waitInt;

    return {
      stopId: generateUUID(),
      locationId: row.locationId,
      tripId: row.tripId ?? null,
      vehicleId: row.vehicleId ?? evaluated.vehicleId,
      arrivalTimeInt: isStart ? null : arrivalInt,
      arrivalTimeStr: isStart || !clock ? null : minutesToClockStr(arrivalInt),
      departureTimeInt: depInt,
      departureTimeStr: depInt == null || !clock ? null : minutesToClockStr(depInt),
      travelCostTo: aware ? num(aware.cost) ?? 0 : 0,
      waitDurationStart: waitInt,
      waitDurationEnd: 0,
      serviceDuration: service,
      stopNumber: i,
      capacityServed: row.capacityServed ?? 0,
      stopType: row.stopType,
      action: row.action ?? null,
      travelDurationFromPreviousStop: agnostic ? num(agnostic.duration) ?? 0 : 0,
      travelDistanceFromPreviousStop: agnostic ? num(agnostic.distance) ?? 0 : 0,
      locationName: row.locationName ?? null,
      color: row.color ?? (vehicle && vehicle.color) ?? null,
      // Carried through so edits don't strip fields the backend now returns.
      // vehicleName follows the vehicle the row is ON (it may have changed in a
      // move); fixedVehicleId is the backend's pin echo and survives verbatim.
      vehicleName: (vehicle && vehicle.row && vehicle.row.vehicleName) ?? row.vehicleName ?? null,
      fixedVehicleId: row.fixedVehicleId ?? null,
      Valid: true,
      validationMessage: null,
    };
  });
}

/**
 * Mirror of the backend's problem_summary_from_result (vrp_results.py:866-932),
 * computed from the live tables. fixedCost mirrors vehicleUsage: a vehicle
 * incurs its fixed cost iff it has at least one non-depot stop.
 */
export function computeResultSummary(data, priorSummary) {
  const stops = data.route_stops || [];
  const workStops = stops.filter((s) => !DEPOT_TYPES.has(s.stopType));
  const assignedLocationIds = new Set(workStops.map((s) => String(s.locationId)));
  const assignedVehicleIds = new Set(stops.map((s) => String(s.vehicleId)));
  const usedVehicleIds = new Set(workStops.map((s) => String(s.vehicleId)));
  const droppedCount = (data.dropped_locations || []).length + (data.dropped_trips || []).length;
  const totalLocations = assignedLocationIds.size + droppedCount;

  const vehiclesById = new Map((data.vehicles || []).map((v) => [String(v.vehicleId), v]));
  let fixedCost = 0;
  for (const id of usedVehicleIds) {
    const v = vehiclesById.get(id);
    if (v) fixedCost += num(v.fixedCost) ?? 0;
  }

  const sum = (field) => stops.reduce((acc, s) => acc + (num(s[field]) ?? 0), 0);
  const totalTravelCost = sum('travelCostTo');

  return {
    assignmentSummary: {
      vehiclesAssigned: { assigned: assignedVehicleIds.size, available: priorSummary?.assignmentSummary?.vehiclesAssigned?.available ?? null },
      locationsAssigned: { assigned: assignedLocationIds.size, total: totalLocations || null },
    },
    routeSummary: {
      totalTravelCost,
      totalMinutesTraveled: sum('travelDurationFromPreviousStop'),
      totalDistanceTraveled: sum('travelDistanceFromPreviousStop'),
    },
    costSummary: { fixedCost, travelCost: totalTravelCost },
    timingSummary: priorSummary?.timingSummary ?? { solveTime: null },
  };
}
